<script lang="ts" setup generic="T extends { status: string }">
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import { Filters } from "~/core/models/filters";
import { formatIsoDate } from "~/core/utils/date";
import { useFetchDtwinOperations } from "~/modules/dtwin/composables/useFetchDtwinOperations";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinFormOperationListColumnConfig } from "~/modules/dtwin/models/DtwinFormOperationListColumnConfig.type";
import { DtwinFormOperationListColumns } from "~/modules/dtwin/models/DtwinFormOperationListColumns.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const props = defineProps<{
  organizationCode: number;
  dtwin: Dtwin;
  dtwinModel: DtwinModel;
  dtwinOperationModel: OperationModel;
  operationType: string;
  reload?: boolean;
  createdInCampaign?: string;
  columns: DtwinFormOperationListColumnConfig[];
}>();

const emit = defineEmits<{
  edit: [operation: DtwinOperation<T>];
  change: [];
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const { error, fetchData, isLoading, onSearch, results, search, setFilter } =
  useFetchDtwinOperations({
    searchMode: SearchMode.FILTER_SEARCH,
    useQueryParametersForPagination: false,
    defaultSort: "-launchPlannedAt",
    organizationCode: currentOrganization.value?.code,
  });

const paginatedList = results as unknown as Ref<PaginatedList<T>>;
const dateFormat = t("operation.dateFormat");

const translatedError: ComputedRef<Error | null | undefined> = computed(() =>
  error.value?.toError(),
);

// Compute the final column configuration
const finalColumnConfig = computed<DtwinFormOperationListColumnConfig[]>(() => {
  if (props.columns) {
    return props.columns;
  }

  // Default columns if nothing is provided
  return [
    DtwinFormOperationListColumns.CREATED_AT,
    DtwinFormOperationListColumns.LAUNCHED_AT,
    DtwinFormOperationListColumns.MODIFIED_AT,
    DtwinFormOperationListColumns.STATUS,
    DtwinFormOperationListColumns.ACTION,
  ];
});

// Helper to check if a column is a custom column
function isCustomColumn(config: DtwinFormOperationListColumnConfig) {
  return typeof config === "object" && "key" in config;
}

/**
 * Retrieve operations
 * @param page
 * @param pageSize
 */
async function loadData(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  setFilter(Filters.DTWIN_OPERATION_UID, props.dtwin?.uid ?? "");
  await fetchData(page, pageSize);
}

function initializeDefaultFilters() {
  setFilter(Filters.DTWIN_OPERATION_TYPE, props.operationType);
  setFilter(
    Filters.DTWIN_CREATED_IN_CAMPAIGN,
    typeof props.createdInCampaign !== "undefined"
      ? props.createdInCampaign
      : "",
  );
}

function reloadWithContext() {
  loadData(
    results.value?.pagination?.page,
    results.value?.pagination?.pageSize,
  );
}

async function onDeviceChange(dtwin?: Dtwin | null) {
  setFilter(Filters.DTWIN_OPERATION_UID, dtwin?.uid ?? "");
  if (dtwin) {
    await search();
  }
}

initializeDefaultFilters();
onSearch(loadData);

watch(() => props.dtwin, onDeviceChange);
watch(() => props.reload, reloadWithContext);

defineExpose({
  reload: reloadWithContext,
});
</script>

<template>
  <lxc-table
    min-width="84rem"
    :is-loading="isLoading"
    :error="translatedError"
    :data="paginatedList?.results"
    :empty-text="t('operation.empty')"
    :context="paginatedList?.pagination"
    data-cy="product-details-operations-table"
    @change-page-and-page-size="loadData"
  >
    <template
      v-for="config in finalColumnConfig"
      :key="typeof config === 'string' ? config : config.key"
    >
      <!-- Built-in columns -->
      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.VERSION"
        prop="customInputParameters.targetVersion"
        :label="t('operation.firmware.update.version')"
        min-width="12rem"
      />

      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.TRIGGER"
        prop="creationMode"
        :label="t('operation.trigger.title')"
        width="15rem"
      >
        <template #default="scope">
          {{ t("operation.trigger." + scope.row.creationMode) }}
        </template>
      </lxc-table-column>

      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.CREATED_AT"
        prop="createdAt"
        :label="t('operation.createdAt')"
        width="15rem"
      >
        <template #default="scope">
          {{ formatIsoDate(scope.row.createdAt, dateFormat) }}
        </template>
      </lxc-table-column>

      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.LAUNCHED_AT"
        prop="launchPlannedAt"
        :label="t('operation.launchedAt')"
        width="15rem"
      >
        <template #default="scope">
          {{ formatIsoDate(scope.row.launchPlannedAt, dateFormat) }}
        </template>
      </lxc-table-column>

      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.MODIFIED_AT"
        prop="modifiedAt"
        :label="t('operation.updatedAt')"
        width="15rem"
      >
        <template #default="scope">
          {{
            scope.row.updatedAt
              ? formatIsoDate(scope.row.updatedAt, dateFormat)
              : ""
          }}
        </template>
      </lxc-table-column>

      <lxc-table-column
        v-if="config === DtwinFormOperationListColumns.STATUS"
        prop="status"
        :label="t('operation.state')"
        width="15rem"
      >
        <template #default="scope">
          <operation-mgr-operation-status-badge :status="scope.row.status" />
        </template>
      </lxc-table-column>

      <lxc-table-column
        v-if="dtwin && config === DtwinFormOperationListColumns.ACTION"
        prop="actions"
        class="!py-0"
        width="12rem"
      >
        <template #default="scope">
          <dtwin-form-operation-list-actions
            :organization-code="organizationCode"
            :dtwin="dtwin"
            :dtwin-model="dtwinModel"
            :dtwin-operation-model="dtwinOperationModel"
            :operation="scope.row"
            @change="reloadWithContext"
            @edit="emit('edit', scope.row)"
          />
        </template>
      </lxc-table-column>

      <!-- Custom columns -->
      <lxc-table-column
        v-if="isCustomColumn(config)"
        :prop="config.prop"
        :label="config.label"
        :width="config.width"
        :min-width="config.minWidth"
      >
        <template #default="scope">
          <slot
            v-if="config.slot"
            :name="`column-${config.slot}`"
            :row="scope.row"
          />

          <template v-else>
            {{ config.prop ? scope.row[config.prop] : "" }}
          </template>
        </template>
      </lxc-table-column>
    </template>
  </lxc-table>
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
